// LT-551 (LT-536): Campaign-from-Brief upload path — upload step + bounded
// clarification round(s). The operator drops a .docx brief; the backend
// (LT-550) extracts a draft Agent + Campaign and returns ≤5 ranked
// clarifications. This page renders the upload control, then the clarification
// form with the LLM's suggested defaults pre-filled, a "Round N of 2"
// indicator (LT-536 decisions #8–10), and an "Accept all defaults" shortcut.
// On ready_for_review it routes to the review page (LT-552). The review is
// always shown — even with zero clarifications (decision #17).
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, UploadCloud } from 'lucide-react';

import PageShell from '../../components/PageShell';
import { Button, Card, Input } from '../../ui';
import {
  campaignBriefAPI,
  extractErrorMessage,
  type BriefExtractionResult,
} from '../../services/api';
import { BRIEF_TEMPLATE_URL } from '../../constants/briefTemplate';

const MAX_CYCLES = 2;

const reviewPath = (campaignId: string) => `/campaigns/upload/${campaignId}/review`;

const CampaignUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Current extraction round (null until the first upload returns).
  const [round, setRound] = useState<BriefExtractionResult | null>(null);
  // Editable answers keyed by clarification id, seeded from suggested_default.
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const goToReview = (result: BriefExtractionResult, defaultsNote: boolean) => {
    navigate(reviewPath(result.campaign_id), {
      state: { defaultsNote, fromUpload: true },
    });
  };

  const seedAnswers = (result: BriefExtractionResult) => {
    const seed: Record<string, string> = {};
    for (const c of result.clarifications) seed[c.id] = c.suggested_default ?? '';
    setAnswers(seed);
  };

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await campaignBriefAPI.fromBrief(file);
      if (result.status === 'ready_for_review') {
        // Zero clarifications — straight to review (decision #17).
        goToReview(result, false);
        return;
      }
      setRound(result);
      seedAnswers(result);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Could not read that brief. Upload a .docx file.'));
    } finally {
      setBusy(false);
    }
  };

  const submitAnswers = async (useDefaults: boolean) => {
    if (!round) return;
    setBusy(true);
    setError(null);
    const payload: Record<string, string> = {};
    for (const c of round.clarifications) {
      // Key answers by question text — the backend matches on question/id.
      payload[c.question] = useDefaults ? (c.suggested_default ?? '') : (answers[c.id] ?? '');
    }
    const wasFinalRound = round.cycle >= MAX_CYCLES;
    try {
      const result = await campaignBriefAPI.answerClarifications(round.campaign_id, payload);
      if (result.status === 'ready_for_review') {
        // At the cycle cap the backend resolves remaining gaps with workspace
        // defaults — flag it so the review page nudges a careful read.
        goToReview(result, wasFinalRound || result.cycle >= MAX_CYCLES);
        return;
      }
      // Another round (only round 2 is possible today).
      setRound(result);
      seedAnswers(result);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Could not submit your answers. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const roundLabel = useMemo(() => {
    if (!round) return '';
    const final = round.cycle >= MAX_CYCLES ? ' — final' : '';
    return `Round ${round.cycle} of ${MAX_CYCLES}${final}`;
  }, [round]);

  return (
    <PageShell>
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/campaigns/create')}
          className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 mb-3"
        >
          <ArrowLeft size={14} strokeWidth={2} />
          Back
        </button>
        <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
          Upload a brief
        </div>
        <h1 className="text-2xl font-semibold text-slate-50">
          {round ? 'A few quick questions' : 'Upload your campaign brief'}
        </h1>
        <p className="text-sm text-slate-400 mt-1 max-w-2xl">
          {round
            ? 'We read your brief and drafted the campaign. Confirm or tweak these to fill the gaps — then you’ll review everything before launch.'
            : 'Drop in a one-page .docx brief. We’ll draft the agent and campaign, then ask up to five clarifying questions.'}
        </p>
      </header>

      {error ? (
        <div className="mb-4 rounded-ds-lg px-3 py-2 text-sm border bg-danger-tint border-danger-border text-danger-text max-w-2xl">
          {error}
        </div>
      ) : null}

      {!round ? (
        <Card elevation="raised" className="flex flex-col gap-4 max-w-2xl">
          <input
            ref={fileRef}
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center gap-2 rounded-ds-xl border border-dashed border-white/15 bg-surface-1 px-6 py-10 text-center hover:border-accent/40 transition-colors"
          >
            <UploadCloud size={28} strokeWidth={2} className="text-accent" />
            <span className="text-sm text-slate-200">
              {file ? file.name : 'Choose a .docx brief'}
            </span>
            <span className="text-xs text-slate-500">
              {file ? 'Click to choose a different file' : 'or click to browse'}
            </span>
          </button>
          <div className="flex items-center justify-between gap-3">
            <a
              href={BRIEF_TEMPLATE_URL}
              download
              className="text-xs text-accent hover:text-accent-hover hover:underline"
            >
              Download the brief template (.docx)
            </a>
            <Button intent="primary" onClick={handleUpload} disabled={!file || busy}>
              <FileUp size={16} strokeWidth={2.5} />
              {busy ? 'Reading…' : 'Extract campaign'}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            No Word? The template opens for free in Google Docs, Word Online,
            Apple Pages or LibreOffice — or skip the document and{' '}
            <button
              type="button"
              onClick={() => navigate('/campaigns/new')}
              className="text-accent hover:text-accent-hover hover:underline"
            >
              build it with the guided form
            </button>
            .
          </p>
        </Card>
      ) : (
        <Card elevation="raised" className="flex flex-col gap-5 max-w-2xl">
          <div className="text-[11px] font-mono uppercase tracking-wider text-accent">
            {roundLabel}
          </div>
          <div className="flex flex-col gap-4">
            {round.clarifications.map((c) => (
              <Input
                key={c.id}
                id={`clar-${c.id}`}
                label={c.question}
                hint={c.suggested_default ? `Suggested: ${c.suggested_default}` : undefined}
                value={answers[c.id] ?? ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [c.id]: e.target.value }))}
                disabled={busy}
              />
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 pt-1">
            <Button intent="ghost" onClick={() => submitAnswers(true)} disabled={busy}>
              Accept all defaults
            </Button>
            <Button intent="primary" onClick={() => submitAnswers(false)} disabled={busy}>
              {busy ? 'Saving…' : 'Continue to review'}
            </Button>
          </div>
        </Card>
      )}
    </PageShell>
  );
};

export default CampaignUploadPage;
